import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostScalar } from './client'

/**
 * Panourile filei Remote Control a tichetului
 * (/api/v2/easeedesk/tickets/{id}/remote-control/{device}/...), perechile
 * capetelor din controllers/Remote_control.php.
 */

const base = (ticket: number, device: number) => `/easeedesk/tickets/${ticket}/remote-control/${device}`

export type RcTabKind = 'quick' | 'operations' | 'standard' | 'screen' | 'bat' | 'responses'

export interface RcTab {
  key: string
  label: string
  icon: string
  kind: RcTabKind
  /** kind = screen: cheia ecranului fisei de aparat */
  href?: string
  shell?: string
  /** kind = operations / standard: categoria */
  category?: number
  /** kind = quick: cheia diagnosticului */
  diagnostic?: string
}

export interface RcCategoryPage {
  ticket: number
  key: string
  title: string
  device: { id: number; name: string; unique: string; type: number }
  tabs: RcTab[]
}

export function rcCategoryQuery(ticket: number, device: number, key: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'category', key],
    queryFn: () => apiGet<RcCategoryPage>(`${base(ticket, device)}/category/${key}`),
  })
}

export interface RcCommand {
  id: number
  name: string
  type: 'CMD' | 'BAT' | 'PSHELL'
  type_name: string
  has_parameters: boolean
  info: string
  color: string
}

export interface RcSubcategory {
  id: number
  name: string
  commands: RcCommand[]
}

export interface RcOperationsPage {
  ticket: number
  device: number
  category: number
  search: string
  subcategories: RcSubcategory[]
  server_subcategories: RcSubcategory[]
  antivirus: boolean
  uninstallers: string[]
}

export function rcOperationsQuery(ticket: number, device: number, category: number, search: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'operations', category, search],
    queryFn: () => apiGet<RcOperationsPage>(`${base(ticket, device)}/operations/${category}?search=${encodeURIComponent(search)}`),
  })
}

/** O comanda din formular, ca in OperationsCommands / x-editable din remote_control.js. */
export type RcOperation = Record<string, PostScalar> & { cmdId: number; type: string; informClient: 0 | 1; customerConfirmation: 0 | 1 }

export function rcSendOperations(ticket: number, device: number, commands: RcOperation[]) {
  return apiPost<{ success: boolean; responseDate: string; sent: number }>(`${base(ticket, device)}/operations`, { commands })
}

export function rcDeviceCommand(ticket: number, device: number, command: 'force-restart' | 'soft-restart' | 'shutdown' | 'log-off') {
  return apiPost<{ ok: boolean }>(`${base(ticket, device)}/device-command`, { command })
}

export function rcCustomerConfirmation(ticket: number, device: number) {
  return apiPost<{ ok: boolean }>(`${base(ticket, device)}/customer-confirmation`, {})
}

export function rcInstallMcAfee(ticket: number, device: number) {
  return apiPost<{ ok: boolean; message?: string }>(`${base(ticket, device)}/mcafee`, {})
}

export function rcMalwareScan(ticket: number, device: number, full: boolean) {
  return apiPost<{ ok: boolean }>(`${base(ticket, device)}/malware-scan`, { full: full ? 1 : 0 })
}

export function rcUninstallAntivirus(ticket: number, device: number, shortName: string) {
  return apiPost<{ ok: boolean; message?: string }>(`${base(ticket, device)}/uninstall-antivirus`, { short_name: shortName })
}

export interface RcBatGroup {
  details: { id: number; name: string; command: string }
  sub_commands: { id: number; name: string; command: string }[]
}

export function rcBatCommandsQuery(ticket: number, device: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'bat'],
    queryFn: () => apiGet<{ groups: RcBatGroup[] }>(`${base(ticket, device)}/bat-commands`),
  })
}

export function rcSendBat(ticket: number, device: number, command: string) {
  return apiPost<{ ok: boolean; message?: string }>(`${base(ticket, device)}/bat`, { command })
}

export function rcClearStartup(ticket: number, device: number, informUser: boolean) {
  return apiPost<{ ok: boolean }>(`${base(ticket, device)}/clear-startup`, informUser ? { inform_user: 'on' } : {})
}

export interface RcStandardCommand {
  id: number
  command: string
  title: string
  info: string
  message: string
}

export interface RcStandardPage {
  ticket: number
  device: number
  category: number
  types: { type_id: number; type_name: string; commands: RcStandardCommand[] }[]
}

export function rcStandardQuery(ticket: number, device: number, category: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'standard', category],
    queryFn: () => apiGet<RcStandardPage>(`${base(ticket, device)}/standard/${category}`),
    staleTime: Infinity,
  })
}

export interface RcStandardValue {
  command_id: number
  command: string
  old_value: string | null
  new_value: string | null
  updated: string
}

export function rcStandardValues(ticket: number, device: number, category: number) {
  return apiGet<RcStandardValue[]>(`${base(ticket, device)}/standard/${category}/values`)
}

export function rcRunStandard(ticket: number, device: number, category: number, commands: Record<number, string>, restore: boolean, informClient: boolean) {
  const fields: Record<string, string | number> = { restore: restore ? 1 : 0, inform_client: informClient ? 1 : 0 }
  for (const [id, command] of Object.entries(commands)) fields[`commands[${id}]`] = command
  return apiPost<{ ok: boolean }>(`${base(ticket, device)}/standard/${category}`, fields)
}

export interface RcResponseRow {
  id: number
  created: string
  command_name: string
  command: string
  status: string
  response: string
  user: string
}

export function rcResponsesQuery(ticket: number, device: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'responses'],
    queryFn: () => apiGet<{ rows: RcResponseRow[] }>(`${base(ticket, device)}/responses`),
    staleTime: 0,
    gcTime: 0,
  })
}

/** Insigna de pe fila Events (getUnviewedCommandsResponses, la 5 s in remote_control.js). */
export function rcUnviewedQuery(ticket: number, device: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'remote-control', device, 'unviewed'],
    queryFn: () => apiGet<{ unviewed: number }>(`${base(ticket, device)}/responses/unviewed`),
    refetchInterval: 5000,
  })
}

export interface RcDiagnostic {
  action_id: number
  updated: number | null
  response: unknown
}

export function rcStartDiagnostic(ticket: number, device: number, key: string, experience: boolean, fresh: boolean) {
  return apiPost<RcDiagnostic>(`${base(ticket, device)}/${experience ? 'experience' : 'diagnostic'}/${key}`, fresh ? { fresh: 1 } : {})
}

export function rcDiagnosticResponse(ticket: number, device: number, experience: boolean, actionId: number) {
  return apiGet<{ action_id: number; response: unknown }>(`${base(ticket, device)}/${experience ? 'experience' : 'diagnostic'}/${actionId}/response`)
}
